import { useEffect, useState, useCallback } from 'react';
import { EFOStatistics } from '../@types';
import { usePreconfiguredFetch } from './';
import { formatDateToYyyyMMdd, getErrorMessage } from '../utils';

const EFO_API_ENDPOINT = process.env.REACT_APP_EFO_API_ENDPOINT;

export const useNotificationStatistics = (taxNumber: string, date: string) => {
    const { fetchResource } = usePreconfiguredFetch();
    const [loading, setLoading] = useState<boolean>(true);
    const [statistics, setStatistics] = useState<EFOStatistics | null>(null);
    const [originalWorkdaysAtOtherCompany, setOriginalWorkdaysAtOtherCompany] = useState<number>(0);
    const [workdaysAtOtherCompany, setWorkdaysAtOtherCompany] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);

    const fetchStatistics = useCallback(async () => {
        setError(null);
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('date', formatDateToYyyyMMdd(new Date(date)));

            const response = await fetchResource(`${EFO_API_ENDPOINT}/notification/statistics/${taxNumber}?${params.toString()}`);

            if (!response.ok) {
                setError(getErrorMessage(response));
                return;
            }

            const data: EFOStatistics = await response.json();
            setStatistics(data);
            setOriginalWorkdaysAtOtherCompany(data.workdaysAtOtherCompany ?? 0);
            setWorkdaysAtOtherCompany(data.workdaysAtOtherCompany ?? 0);
        } catch (e) {
            console.error('Error fetching notification statistics:', e);
        } finally {
            setLoading(false);
        }
    }, [taxNumber, fetchResource, date]);

    const updateWorkdaysAtOtherCompany = useCallback(async () => {
        setError(null);
        setLoading(true);
        try {
            const response = await fetchResource(`${EFO_API_ENDPOINT}/notification/external-workdays`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    taxIdNumber: taxNumber,
                    days: workdaysAtOtherCompany,
                }),
            });

            if (!response.ok) {
                setError(getErrorMessage(response));
                return;
            }

            const updatedValue = await response.json();
            setOriginalWorkdaysAtOtherCompany(updatedValue);
            setWorkdaysAtOtherCompany(updatedValue);
        } catch (e) {
            console.error('Error updating workdays at other company:', e);
        } finally {
            setLoading(false);
        }
    }, [fetchResource, workdaysAtOtherCompany]);

    useEffect(() => {
        fetchStatistics();
    }, [fetchStatistics]);

    return {
        loading,
        statisticsList: statistics?.statistics ?? [],
        consecutiveDays: statistics?.consecutiveDays,
        originalWorkdaysAtOtherCompany,
        workdaysAtOtherCompany,
        setWorkdaysAtOtherCompany,
        updateWorkdaysAtOtherCompany,
        refetch: fetchStatistics,
        error,
    };
};
